// VOY-485 (rebuild) — Traveler Notes (Settings tab) — DECLARED INPUT.
//
// Builds VOY-459's UI (orphaned by the previous ticket rescope) against
// its documented endpoint contracts (see VOY-459 Linear comments):
//
//   GET    /api/v2/profile/:userId/notes?scope=<NoteScope>
//   POST   /api/v2/profile/:userId/notes
//   PATCH  /api/v2/profile/:userId/notes/:id
//   DELETE /api/v2/profile/:userId/notes/:id   (soft-delete)
//
// All four wired through the shipped lib helpers; no new endpoints. We
// group notes by scope (the 7-value `NoteScope` enum), surface
// `hard_constraint` and `dealbreaker` with visual emphasis (those are
// INVIOLABLE for the agent per VOY-416's prompt), and offer inline
// create / edit / delete per row.
//
// Validation matches the backend: content trimmed, 1–2000 chars,
// importance 1–5. The backend re-validates and 400s on bad input —
// we surface the message verbatim.
//
// Companion-specific scope: requires `related_companion_id`. The create
// form shows a companion-picker only when scope='companion_specific'.
// trip_specific scope: requires `related_trip_id` — we don't surface
// trip-picking here yet (no trip-picker component exists). Users can
// still create freeform notes about specific trips for now; trip_specific
// scope will be unlocked when VOY-462 ships its trip-picker.

import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react'
import { Plus, Pencil, Trash2, Star } from 'lucide-react'
import {
  getTravelerNotes,
  createTravelerNote,
  updateTravelerNote,
  deleteTravelerNote,
  getTravelCompanions,
} from '@/lib/profile'
import type { TravelerNote, NoteScope, TravelCompanion } from '@/types/profile'

export interface TravelerNotesSectionProps {
  userId: string
}

const SCOPE_OPTIONS: { value: NoteScope; label: string; inviolable: boolean; hint: string }[] = [
  { value: 'hard_constraint', label: 'Hard constraint', inviolable: true, hint: 'Voy will never violate this (e.g. "wife allergic to shellfish").' },
  { value: 'dealbreaker', label: 'Dealbreaker', inviolable: true, hint: 'Voy will never recommend this (e.g. "never all-inclusives").' },
  { value: 'identity', label: 'About me', inviolable: false, hint: 'Who you are as a traveler.' },
  { value: 'soft_preference', label: 'Soft preference', inviolable: false, hint: 'Voy weighs this but it isn\'t a hard rule.' },
  { value: 'companion_specific', label: 'About a companion', inviolable: false, hint: 'Links to a saved companion.' },
  { value: 'freeform', label: 'Other', inviolable: false, hint: 'Anything else worth Voy knowing.' },
]

const SCOPE_LABEL: Record<NoteScope, string> = SCOPE_OPTIONS.reduce((acc, s) => ({ ...acc, [s.value]: s.label }), { trip_specific: 'About a trip' } as Record<NoteScope, string>)

const GROUP_ORDER: NoteScope[] = [
  'hard_constraint',
  'dealbreaker',
  'identity',
  'soft_preference',
  'companion_specific',
  'trip_specific',
  'freeform',
]

const STARTER_PROMPTS = [
  '"Wife is allergic to shellfish"  — hard constraint',
  '"We never do all-inclusive resorts"  — dealbreaker',
  '"I\'m vegetarian"  — about me',
  '"Always book aisle seats on long-haul"  — soft preference',
  '"Kids hate buffets and crowded pools"  — about a companion',
]

interface NoteDraft {
  content: string
  scope: NoteScope
  importance: number
  related_companion_id: string | null
}

const EMPTY_DRAFT: NoteDraft = {
  content: '',
  scope: 'soft_preference',
  importance: 3,
  related_companion_id: null,
}

export function TravelerNotesSection({ userId }: TravelerNotesSectionProps) {
  const [notes, setNotes] = useState<TravelerNote[]>([])
  const [companions, setCompanions] = useState<TravelCompanion[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<NoteDraft>(EMPTY_DRAFT)
  const [isCreating, setIsCreating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) return
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  async function reload() {
    setLoading(true)
    try {
      const [n, c] = await Promise.all([
        getTravelerNotes(userId).catch(() => [] as TravelerNote[]),
        getTravelCompanions(userId).catch(() => [] as TravelCompanion[]),
      ])
      setNotes(n)
      setCompanions(c)
    } finally {
      setLoading(false)
    }
  }

  const grouped = useMemo(() => {
    const out: Record<NoteScope, TravelerNote[]> = {
      hard_constraint: [], dealbreaker: [], identity: [],
      soft_preference: [], companion_specific: [], trip_specific: [], freeform: [],
    }
    for (const n of notes) (out[n.scope] ??= []).push(n)
    return out
  }, [notes])

  function beginCreate() {
    setIsCreating(true)
    setEditingId(null)
    setDraft(EMPTY_DRAFT)
    setError(null)
  }

  function beginEdit(note: TravelerNote) {
    setIsCreating(false)
    setEditingId(note.id)
    setDraft({
      content: note.content,
      scope: note.scope,
      importance: note.importance,
      related_companion_id: note.related_companion_id ?? null,
    })
    setError(null)
  }

  function cancel() {
    setIsCreating(false)
    setEditingId(null)
    setDraft(EMPTY_DRAFT)
    setError(null)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const content = draft.content.trim()
      if (!content) {
        setError('Note content is required.')
        return
      }
      if (content.length > 2000) {
        setError('Note must be 2000 characters or fewer.')
        return
      }
      const isCompanionScope = draft.scope === 'companion_specific'
      if (isCompanionScope && !draft.related_companion_id) {
        setError('Pick a companion this note is about.')
        return
      }
      // CreateTravelerNoteInput defines related_companion_id / related_trip_id
      // as `string | undefined` (omitted when not set). PATCH accepts
      // `string | null` (an explicit `null` clears the link). Build the
      // right shape for each path.
      if (editingId) {
        await updateTravelerNote(userId, editingId, {
          content,
          scope: draft.scope,
          importance: draft.importance,
          related_companion_id: isCompanionScope ? draft.related_companion_id : null,
          related_trip_id: null,
        })
      } else {
        await createTravelerNote(userId, {
          content,
          scope: draft.scope,
          importance: draft.importance,
          ...(isCompanionScope && draft.related_companion_id
            ? { related_companion_id: draft.related_companion_id }
            : {}),
        })
      }
      cancel()
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.')
    } finally {
      setSaving(false)
    }
  }

  async function remove(id: string) {
    // Confirm before deleting. hard_constraint + dealbreaker notes are
    // INVIOLABLE agent constraints — a misclick can destroy a rule the
    // user relied on the agent to enforce. The other scopes are less
    // destructive but a confirm here is cheap insurance across the
    // board (same posture as TravelCompanionsSection).
    const note = notes.find((n) => n.id === id)
    const inviolable = note?.scope === 'hard_constraint' || note?.scope === 'dealbreaker'
    const msg = inviolable
      ? `Delete this ${note?.scope === 'dealbreaker' ? 'dealbreaker' : 'hard constraint'}?\n\n"${note?.content?.slice(0, 120) ?? ''}"\n\nThe agent will stop enforcing this immediately.`
      : `Delete this note?\n\n"${note?.content?.slice(0, 120) ?? ''}"`
    if (!window.confirm(msg)) return
    setSaving(true)
    try {
      await deleteTravelerNote(userId, id)
      if (editingId === id) cancel()
      await reload()
    } catch (err) {
      console.error('[TravelerNotesSection] delete failed:', err)
    } finally {
      setSaving(false)
    }
  }

  const total = notes.length
  const editing = editingId !== null || isCreating

  return (
    <section className="card" aria-labelledby="notes-h">
      <div className="card-h">
        <h3 id="notes-h">Traveler notes</h3>
        <div className="row" style={{ gap: 8 }}>
          <span className="meta">{loading ? 'LOADING…' : `${total} NOTE${total === 1 ? '' : 'S'}`}</span>
          {!editing && (
            <button type="button" className="btn-ghost" style={{ padding: '5px 12px', fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={beginCreate}>
              <Plus aria-hidden="true" style={{ width: 12, height: 12 }} />
              Add
            </button>
          )}
        </div>
      </div>

      {!loading && total === 0 && !editing && (
        <div className="stack sm" style={{ gap: 8 }}>
          <p style={{ color: 'var(--v6-fg-muted)', fontSize: 13, margin: 0 }}>
            Tell Voy about anything that matters when you travel.
          </p>
          <ul className="starter-prompts">
            {STARTER_PROMPTS.map((s) => <li key={s}>{s}</li>)}
          </ul>
        </div>
      )}

      {editing && (
        <form onSubmit={submit} className="stack" style={{ gap: 12, marginBottom: 14 }}>
          <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
            <label className="field" style={{ flex: '1 1 200px' }}>
              <span className="field-l">SCOPE</span>
              <select
                value={draft.scope}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setDraft((d) => ({ ...d, scope: e.target.value as NoteScope }))}
              >
                {SCOPE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
            <div className="field" style={{ flex: '1 1 200px' }}>
              <span className="field-l">IMPORTANCE</span>
              <ImportanceStars value={draft.importance} onChange={(v) => setDraft((d) => ({ ...d, importance: v }))} />
            </div>
          </div>
          {draft.scope === 'companion_specific' && (
            <label className="field">
              <span className="field-l">COMPANION</span>
              <select
                value={draft.related_companion_id ?? ''}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setDraft((d) => ({ ...d, related_companion_id: e.target.value || null }))}
              >
                <option value="">Pick a companion…</option>
                {companions.map((c) => <option key={c.id} value={c.id}>{c.display_name}</option>)}
              </select>
            </label>
          )}
          <p className="toggle-row-hint" style={{ marginTop: -6 }}>
            {SCOPE_OPTIONS.find((s) => s.value === draft.scope)?.hint}
          </p>
          <label className="field">
            <span className="field-l">NOTE</span>
            <textarea
              autoFocus
              rows={3}
              value={draft.content}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDraft((d) => ({ ...d, content: e.target.value }))}
              maxLength={2000}
              placeholder="e.g. Wife is allergic to shellfish"
              style={{ resize: 'vertical', fontFamily: 'inherit' }}
            />
          </label>
          {error && <p style={{ color: 'var(--v6-neg)', fontSize: 12, margin: 0 }} role="alert">{error}</p>}
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn-ghost" style={{ padding: '6px 12px', fontSize: 11.5 }} onClick={cancel} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary" style={{ padding: '6px 12px', fontSize: 11.5 }} disabled={saving || !draft.content.trim()}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add note'}
            </button>
          </div>
        </form>
      )}

      <div className="stack" style={{ gap: 14 }}>
        {GROUP_ORDER.map((scope) => {
          const list = grouped[scope]
          if (!list || list.length === 0) return null
          const def = SCOPE_OPTIONS.find((s) => s.value === scope)
          return (
            <div key={scope}>
              <div className="lbl" style={{ marginBottom: 6 }}>
                {SCOPE_LABEL[scope] ?? scope}
                {def?.inviolable && <span style={{ marginLeft: 8, color: 'var(--v6-neg)' }}>INVIOLABLE</span>}
              </div>
              <ul className="note-list">
                {list.map((note) => (
                  <NoteRow
                    key={note.id}
                    note={note}
                    onEdit={() => beginEdit(note)}
                    onDelete={() => void remove(note.id)}
                    companionName={
                      note.related_companion_id
                        ? companions.find((c) => c.id === note.related_companion_id)?.display_name ?? null
                        : null
                    }
                  />
                ))}
              </ul>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function NoteRow({
  note,
  onEdit,
  onDelete,
  companionName,
}: {
  note: TravelerNote
  onEdit: () => void
  onDelete: () => void
  companionName: string | null
}) {
  return (
    <li className="note-row">
      <div className="note-row-content">
        <span className="note-row-text">{note.content}</span>
        {companionName && (
          <span className="note-row-meta">about {companionName}</span>
        )}
      </div>
      <span className="note-row-importance" aria-label={`Importance ${note.importance} of 5`}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className={i < note.importance ? 'on' : ''} aria-hidden="true" />
        ))}
      </span>
      <div className="row" style={{ gap: 4 }}>
        <button type="button" className="note-action" onClick={onEdit} aria-label="Edit note"><Pencil aria-hidden="true" /></button>
        <button type="button" className="note-action danger" onClick={onDelete} aria-label="Delete note"><Trash2 aria-hidden="true" /></button>
      </div>
    </li>
  )
}

function ImportanceStars({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="row" style={{ gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          aria-label={`Importance ${n}`}
          aria-pressed={value === n}
          className="importance-star-btn"
        >
          <Star className={n <= value ? 'on' : ''} aria-hidden="true" />
        </button>
      ))}
    </div>
  )
}
